import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PageManagement } from "../modules/shared/model/page-management.interfaces";
import { PageService } from "../services/pageService";
import { Button } from "../components/ui/Button";
import { ArrowLeft, AlertCircle, Loader2 } from "lucide-react";

const DynamicPage: React.FC = () => {
  const { url } = useParams<{ url: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Reconstruct the full URL path from the current location
  const fullUrl = location.pathname;
  console.log("url param:", url);
  console.log("full URL path:", fullUrl);
  const [pageData, setPageData] = useState<PageManagement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (fullUrl) {
      fetchPageByUrl(fullUrl);
    }
  }, [fullUrl]);

  const fetchPageByUrl = async (pageUrl: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const page = await PageService.getPageByUrl(pageUrl);
      
      if (page) {
        setPageData(page);
      } else {
        setPageData(null);
        setError("Page not found");
      }
    } catch (err) {
      console.error("Error fetching page:", err);
      // Check if it's a JSON parsing error
      if (err instanceof SyntaxError) {
        setError("Invalid data received from server");
      } else {
        setError("Failed to load page");
      }
      setPageData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading... | Kokobeo</title>
        </Helmet>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-600">Loading page...</p>
          </div>
        </div>
      </>
    );
  }

  if (error || !pageData) {
    return (
      <>
        <Helmet>
          <title>Page Not Found | Kokobeo</title>
          <meta name="description" content="The page you're looking for doesn't exist or has been removed." />
        </Helmet>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            <AlertCircle className="h-16 w-16 mx-auto mb-4 text-red-500" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Page Not Found</h1>
            <p className="text-gray-600 mb-6">
              The page your&apos;e looking for doesn&apos;t exist or has been removed.
            </p>
            <div className="space-y-3">
              <Button onClick={handleBackClick} className="w-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go Back
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate("/")}
                className="w-full"
              >
                Go to Homepage
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Check if page is published
  if (Number(pageData.status?.id) !== 2) { // 2 = Published
    return (
      <>
        <Helmet>
          <title>Page Not Available | Kokobeo</title>
          <meta name="description" content="This page is currently not published or is in draft mode." />
        </Helmet>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            <AlertCircle className="h-16 w-16 mx-auto mb-4 text-yellow-500" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Page Not Available</h1>
            <p className="text-gray-600 mb-6">
              This page is currently not published or is in draft mode.
            </p>
            <Button onClick={handleBackClick} className="w-full">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{pageData.title || "Page"} | Kokobeo</title>
        <meta name="description" content={pageData.metaDescription || ""} />
        {pageData.metaKeywords && pageData.metaKeywords.length > 0 && (
          <meta name="keywords" content={pageData.metaKeywords.join(", ")} />
        )}
        <meta property="og:title" content={pageData.title || "Page"} />
        <meta property="og:description" content={pageData.metaDescription || ""} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}${fullUrl}`} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageData.title || "Page"} />
        <meta name="twitter:description" content={pageData.metaDescription || ""} />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          <article className="prose prose-lg max-w-none">
            {/* Page Title */}
            {pageData.title && (
              <header className="mb-8">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">
                  {pageData.title}
                </h1>
              </header>
            )}


            {/* Page Content */}
            <div className="prose prose-lg max-w-none">
              {(() => {
                // Check if content starts with HTML tags (even if isText is true)
                const content = pageData.content || "";
                const startsWithHtmlTag = /^\s*<[^>]+>/.test(content);
                
                // If isText is false OR content starts with HTML tags, render as HTML
                if (!pageData.isText || startsWithHtmlTag) {
                  return (
                    <div 
                      dangerouslySetInnerHTML={{ __html: content }}
                      className="prose prose-lg max-w-none"
                    />
                  );
                }
                
                // Otherwise render as plain text
                return (
                  <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {content}
                  </p>
                );
              })()}
            </div>
          </article>
        </div>
      </div>
    </>
  );
};

export default DynamicPage;
